import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react'
import { getTranslations, isRtlLocale } from './fallbackResources'
import type { AppLocale, TranslationTree } from './types'
import { useLocaleStore } from '../stores/localeStore'

type LocaleContextValue = {
  locale: AppLocale
  t: TranslationTree
  isRtl: boolean
  setLocale: (locale: AppLocale) => void
  toggleLocale: () => void
}

const LocaleContext = createContext<LocaleContextValue | null>(null)

export function LocaleProvider({ children }: { children: ReactNode }) {
  const locale = useLocaleStore((s) => s.locale)
  const setStoreLocale = useLocaleStore((s) => s.setLocale)
  const toggleStoreLocale = useLocaleStore((s) => s.toggleLocale)

  const t = useMemo(() => getTranslations(locale), [locale])
  const isRtl = isRtlLocale(locale)

  useEffect(() => {
    document.documentElement.lang = locale
    document.documentElement.dir = isRtl ? 'rtl' : 'ltr'
  }, [locale, isRtl])

  const setLocale = useCallback(
    (next: AppLocale) => {
      setStoreLocale(next)
    },
    [setStoreLocale]
  )

  const value = useMemo(
    () => ({
      locale,
      t,
      isRtl,
      setLocale,
      toggleLocale: toggleStoreLocale,
    }),
    [locale, t, isRtl, setLocale, toggleStoreLocale]
  )

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext)
  if (!ctx) throw new Error('useLocale must be used within LocaleProvider')
  return ctx
}
